import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientByTelefonoQuery } from '../../services/hooks/useClientes';
import { useRestoreClientSubscription } from '../../services/hooks/useClienteSuscripciones';
import { Cliente } from '../../types/clientes';
import { AxiosError } from 'axios';
import ConfirmationModal from '../common/ConfirmationModal'; // ← Agregar import

interface TelefonoValidacionProps {
    telefono: string;
    onClienteEncontrado?: (cliente: Cliente | null) => void;
    onValidationChange?: (isValid: boolean) => void;
}

export const TelefonoValidacion: React.FC<TelefonoValidacionProps> = ({ 
    telefono,
    onClienteEncontrado,
    onValidationChange 
}) => {
    const navigate = useNavigate();
    const { tenantCode } = useParams<{ tenantCode: string }>();
    const { data: clienteEncontrado, isLoading, error } = useClientByTelefonoQuery(telefono);
    const restoreClientMutation = useRestoreClientSubscription();
    
    // Estado para el modal de confirmación
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Notificar cambios al componente padre
    React.useEffect(() => {
        onClienteEncontrado?.(clienteEncontrado || null);
        onValidationChange?.(!clienteEncontrado); // Es válido si NO encuentra cliente
    }, [clienteEncontrado, onClienteEncontrado, onValidationChange]);

    // No mostrar nada si está cargando o no hay teléfono para buscar
    if (isLoading || !telefono) {
        return null;
    }

    // No hay cliente con ese teléfono - OK (ahora clienteEncontrado será null en lugar de error)
    if (!clienteEncontrado && !error) {
        return (
            <div className="mt-2 p-3 bg-green-100 border border-green-400 rounded-md">
                <div className="flex items-center">
                    <span className="text-green-700 font-medium">✅ Teléfono disponible</span>
                </div>
            </div>
        );
    }

    // Error real (no 404) - problemas de red, servidor, etc.
    if (error && (error as AxiosError)?.response?.status !== 404) {
        return (
            <div className="mt-2 p-3 bg-yellow-100 border border-yellow-400 rounded-md">
                <div className="flex items-center">
                    <span className="text-yellow-700 font-medium">⚠️ Error al verificar teléfono</span>
                </div>
            </div>
        );
    }

    const handleRestaurar = async () => {
        if (!clienteEncontrado) return;

        const subscriptionId = clienteEncontrado.suscripciones && 
            clienteEncontrado.suscripciones.length > 0 
            ? clienteEncontrado.suscripciones[0]._id 
            : null;

        try {
            const response = await restoreClientMutation.mutateAsync({
                clientId: clienteEncontrado._id,
                subscriptionId
            });
            
            // Cerrar modal y redirigir
            setShowConfirmModal(false);
            navigate(`/${tenantCode}/clientes/${response.clientId}`);
        } catch (error) {
            // El error ya se maneja en el hook
            console.error('Error restaurando cliente:', error);
        }
    };

    // Cliente encontrado
    if (clienteEncontrado) {
        const isActivo = clienteEncontrado.activo;
        
        return (
            <>
                <div className={`mt-2 p-3 border rounded-md ${
                    isActivo 
                        ? 'bg-red-100 border-red-400' 
                        : 'bg-yellow-100 border-yellow-400'
                }`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <span className={`font-medium ${
                                isActivo ? 'text-red-700' : 'text-yellow-700'
                            }`}>
                                {isActivo ? '🔴' : '⚠️'} Cliente encontrado: {clienteEncontrado.nombre}
                            </span>
                            <p className={`text-sm mt-1 ${
                                isActivo ? 'text-red-600' : 'text-yellow-600'
                            }`}>
                                {isActivo 
                                    ? 'Este cliente ya existe y está activo'
                                    : 'Este cliente existe pero está inactivo - ¿Desea restaurarlo?'
                                }
                            </p>
                        </div>
                        {!isActivo && (
                            <button
                                type="button"
                                disabled={restoreClientMutation.isPending}
                                className="ml-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:opacity-50"
                                onClick={() => setShowConfirmModal(true)} // ← Abrir modal en lugar de restaurar directamente
                            >
                                {restoreClientMutation.isPending ? 'Restaurando...' : 'Restaurar Cliente'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Modal de confirmación */}
                <ConfirmationModal
                    isOpen={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    onConfirm={handleRestaurar}
                    confirmMessage={`¿Está seguro que desea restaurar al cliente <strong>${clienteEncontrado.nombre}</strong>?<br/><br/>Esto reactivará el cliente y su suscripción.`}
                />
            </>
        );
    }

    return null;
};